import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  SOURCE_TOOL_MAX_LENGTH,
} from '@atlas/api-contract';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { toDisplayableError } from '@/components/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import {
  DUPLICATE_PROTOTYPE_NAME_MESSAGE,
  isDuplicateNameError,
  prototypeFormSchema,
  type PrototypeFormValues,
} from './prototype-schemas';

export interface PrototypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  submitLabel: string;
  /** Verb shown while the request is in flight, e.g. "Creating…". */
  pendingLabel: string;
  /** Seeded into the form each time the dialog opens. */
  defaultValues: PrototypeFormValues;
  /**
   * Performs the mutation. Resolving `true` closes the dialog; resolving
   * `false` leaves it open untouched (the caller decided not to proceed).
   * Rejections are rendered here — a 409 on the name field, anything else at
   * the top of the form.
   */
  onSubmit: (values: PrototypeFormValues) => Promise<boolean>;
}

/**
 * The create and edit forms for a prototype are the same form.
 *
 * Same argument as `ProjectFormDialog`: both write the same three fields against
 * the same schema and both have to turn the same 409 into the same inline
 * message, so the shape lives here once and the two dialogs differ only in their
 * wording and their mutation.
 *
 * Failure never clears the form. Whatever was typed is still there, the error
 * sits next to the field that caused it, and the only thing the user has to
 * change is the thing that was wrong.
 */
export const PrototypeFormDialog = ({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  pendingLabel,
  defaultValues,
  onSubmit,
}: PrototypeFormDialogProps) => {
  const form = useForm<PrototypeFormValues>({
    resolver: zodResolver(prototypeFormSchema),
    defaultValues,
  });

  const { reset } = form;

  /*
   * Seed on open, not on `defaultValues` identity: the caller builds a fresh
   * object every render, and re-seeding on that would wipe what is being typed.
   */
  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;

  useEffect(() => {
    if (open) {
      reset(defaultValuesRef.current);
    }
  }, [open, reset]);

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      if (await onSubmit(values)) {
        onOpenChange(false);
      }
    } catch (error: unknown) {
      if (isDuplicateNameError(error)) {
        form.setError('name', {
          type: 'server',
          message: DUPLICATE_PROTOTYPE_NAME_MESSAGE,
        });
        form.setFocus('name');
        return;
      }

      form.setError('root', {
        type: 'server',
        message: toDisplayableError(error),
      });
    }
  });

  const isSubmitting = form.formState.isSubmitting;
  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {rootError === undefined ? null : (
              <p
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {rootError}
              </p>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      autoComplete="off"
                      maxLength={NAME_MAX_LENGTH}
                      placeholder="Checkout flow"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      maxLength={DESCRIPTION_MAX_LENGTH}
                      placeholder="What this prototype covers, and what feedback is wanted."
                      className="resize-none"
                    />
                  </FormControl>
                  <FormDescription>
                    Optional. Shown on the prototype card.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sourceTool"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source tool</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      maxLength={SOURCE_TOOL_MAX_LENGTH}
                      placeholder="Figma"
                    />
                  </FormControl>
                  <FormDescription>
                    Optional. Where the HTML was exported from — useful when a
                    reviewer asks for a change the export cannot make.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={isSubmitting}
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 aria-hidden="true" className="animate-spin" />
                    {pendingLabel}
                  </>
                ) : (
                  submitLabel
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
